import { listConversationsSuspenseQueryOptions } from "@packages/api-client/hooks/chatController";
import { createFileRoute, Link, Outlet } from "@tanstack/react-router";
import { Brain, PanelLeft, Plus } from "lucide-react";
import { Suspense, useState } from "react";
import { useTranslation } from "react-i18next";
import { AssistantMemories } from "@/components/chat/AssistantMemories";
import {
	Sheet,
	SheetContent,
	SheetHeader,
	SheetTitle,
} from "@/components/ui/sheet";
import { requireReachableRoute } from "@/lib/offline/routeGuard";
import { ConversationSidebar } from "./assistant/-components/ConversationSidebar";

export const Route = createFileRoute("/_auth/app/_app/org/$orgId/assistant")({
	staticData: { offline: "online-only" },
	beforeLoad: () => requireReachableRoute("online-only"),
	loader: async ({ context, params }) => {
		// Prefetch conversation list so sidebar renders immediately
		context.queryClient.ensureQueryData(
			listConversationsSuspenseQueryOptions(params.orgId, {
				page: 1,
				limit: 30,
			}),
		);
	},
	component: AssistantLayout,
});

function AssistantLayout() {
	const { t } = useTranslation();
	const { orgId } = Route.useParams();
	const [drawerOpen, setDrawerOpen] = useState(false);
	const [memoriesOpen, setMemoriesOpen] = useState(false);

	const historyLabel = t("grass.assistant.conversations", {
		defaultValue: "Conversas",
	});
	const newConversationLabel = t("grass.assistant.newConversation", {
		defaultValue: "Nova conversa",
	});

	return (
		<div className="flex h-full overflow-hidden">
			{/* Desktop conversation sidebar */}
			<aside className="hidden md:flex w-64 shrink-0 overflow-hidden border-r border-base-300">
				<Suspense fallback={<SidebarSkeleton />}>
					<ConversationSidebar />
				</Suspense>
			</aside>

			{/* Chat column */}
			<div className="flex-1 flex flex-col overflow-hidden min-w-0 bg-base-200/40">
				<div className="flex justify-end border-b border-base-300 px-2 py-1">
					<button
						type="button"
						className="btn btn-ghost btn-sm gap-2"
						onClick={() => setMemoriesOpen(true)}
					>
						<Brain className="h-4 w-4" />
						{t("grass.assistant.memories")}
					</button>
				</div>

				{/* Mobile header: history drawer toggle + new chat */}
				<div className="md:hidden flex items-center justify-between gap-2 border-b border-base-300 bg-base-100 px-2 py-1">
					<button
						type="button"
						onClick={() => setDrawerOpen(true)}
						aria-label={historyLabel}
						className="btn btn-ghost btn-sm btn-square"
					>
						<PanelLeft className="h-5 w-5" />
					</button>
					<span className="font-semibold text-sm truncate">
						{t("grass.assistant.title", { defaultValue: "Assistente" })}
					</span>
					<Link
						to="/app/org/$orgId/assistant"
						params={{ orgId }}
						aria-label={newConversationLabel}
						className="btn btn-ghost btn-sm btn-square"
					>
						<Plus className="h-5 w-5" />
					</Link>
				</div>

				<main className="flex-1 flex flex-col overflow-hidden min-h-0">
					<Outlet />
				</main>
			</div>

			<Sheet open={memoriesOpen} onOpenChange={setMemoriesOpen}>
				<SheetContent className="w-full sm:max-w-lg flex flex-col bg-base-100">
					<SheetHeader>
						<SheetTitle>{t("grass.assistant.memories")}</SheetTitle>
					</SheetHeader>
					<Suspense fallback={<SidebarSkeleton />}>
						{memoriesOpen && <AssistantMemories organizationId={orgId} />}
					</Suspense>
				</SheetContent>
			</Sheet>

			{/* Mobile conversation drawer */}
			<Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
				<SheetContent
					side="left"
					className="w-[85vw] max-w-sm p-0 flex flex-col bg-base-100"
				>
					<SheetHeader className="px-3 py-3 border-b border-base-300">
						<SheetTitle className="text-base">{historyLabel}</SheetTitle>
					</SheetHeader>
					<div className="flex-1 min-h-0">
						<Suspense fallback={<SidebarSkeleton />}>
							<ConversationSidebar onNavigate={() => setDrawerOpen(false)} />
						</Suspense>
					</div>
				</SheetContent>
			</Sheet>
		</div>
	);
}

const SKELETON_KEYS = ["a", "b", "c", "d", "e", "f"] as const;

function SidebarSkeleton() {
	return (
		<div className="h-full w-full p-3">
			<div className="space-y-2">
				{SKELETON_KEYS.map((k) => (
					<div key={k} className="skeleton h-10 w-full rounded-lg" />
				))}
			</div>
		</div>
	);
}
